import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { EVENTO_FICHA } from '@/lib/eventos';

/**
 * api.ts — el contrato de Mi perfil.
 *
 * Los tipos están escritos a mano y eso es **deuda declarada, no una decisión**:
 * el backend tiene un solo `response_model` en 27 routers, así que
 * `/openapi.json` no describe ninguna respuesta y no hay nada que generar. En
 * cuanto los routers declaren su modelo, esto se sustituye por los tipos
 * generados y el compilador empieza a avisar cuando el backend cambia de forma.
 * Hasta entonces son una promesa. Están copiados leyendo `page-profile.js` y
 * los routers, campo por campo.
 *
 * Los endpoints NO cambian. Son exactamente los seis que ya usaba la pantalla
 * vanilla, con los mismos parámetros: esta migración es de presentación, y el
 * servidor no se entera de nada.
 */

export interface CursoDelPerfil {
  course_id: number;
  course_title?: string;
  progress_pct: number;
  completed_at?: string | null;
  quiz_score?: number | null;
  has_certificate?: boolean;
}

export interface Perfil {
  full_name?: string;
  email?: string;
  phone?: string | null;
  extension?: string | null;
  city?: string | null;
  birth_date_display?: string | null;
  area_name?: string | null;
  manager_name?: string | null;
  hire_date?: string | null;
  position?: string | null;
  sede_name?: string | null;
  is_active?: boolean;
  bio?: string | null;
  skills?: string | null;
  avatar_url?: string | null;
  courses?: CursoDelPerfil[];
}

export interface Certificado {
  course_id: number;
  course_title?: string;
  issued_at: string;
  expires_at?: string | null;
  status: 'vigente' | 'por_vencer' | 'vencido';
  download_url?: string;
  renewal_count?: number;
}

export interface Kudo {
  id?: number;
  badge?: string;
  message?: string | null;
}

export interface Consentimiento {
  doc_key: string;
  title?: string;
  version?: string;
  accepted?: boolean;
  decidido?: boolean;
  accepted_at?: string | null;
  /** Un obligatorio no es revocable: el acceso al portal depende de él y el
   *  servidor responde 400 si se intenta. Por eso no se le ofrece botón. */
  revocable?: boolean;
}

export interface CampoFicha {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'employee' | 'number' | 'date' | 'email' | 'phone';
  editable: boolean;
  required?: boolean;
  options?: string[];
  sensible?: boolean;
  /** Por qué NO es editable. Se enseña al lado del campo apagado: un campo
   *  bloqueado sin motivo es un callejón sin salida. */
  motivo?: string;
}

export interface MiFicha {
  campos?: CampoFicha[];
  ficha?: Record<string, string | number | null>;
  /** Etiquetas de lo que falta por llenar. Lo calcula el servidor. */
  pendientes?: string[];
}

/** Claves de caché centralizadas: si se escriben sueltas en cada sitio, una
 *  invalidación acaba fallando por una letra y nadie se entera hasta que un
 *  dato guardado no se refresca. */
export const claves = {
  perfil: ['perfil'] as const,
  certificados: ['perfil', 'certificados'] as const,
  kudos: (userId?: number) => ['perfil', 'kudos', userId] as const,
  consentimientos: ['perfil', 'consentimientos'] as const,
  ficha: ['perfil', 'ficha'] as const,
};

export function usePerfil() {
  return useQuery({ queryKey: claves.perfil, queryFn: () => api.get<Perfil>('/users/me/profile') });
}

export function useCertificados() {
  return useQuery({
    queryKey: claves.certificados,
    queryFn: () => api.get<Certificado[]>('/certificates/my'),
  });
}

export function useKudos(userId?: number) {
  return useQuery({
    queryKey: claves.kudos(userId),
    // El filtro va en el SERVIDOR. Antes se pedían los 50 kudos más recientes de
    // toda la empresa y se descartaba en el navegador lo que no era de esta
    // persona: pasada esa cifra, los reconocimientos propios desaparecían del
    // propio perfil sin aviso. Se corrigió el 2026-08 y se conserva.
    queryFn: () => api.get<Kudo[]>('/kudos', { receiver_id: userId ?? 0, limit: 50 }),
    enabled: userId != null,
  });
}

export function useConsentimientos() {
  return useQuery({
    queryKey: claves.consentimientos,
    queryFn: () => api.get<Consentimiento[]>('/legal/my-consents'),
  });
}

/** La ficha se pide SIEMPRE al entrar al perfil, no solo al abrir el panel.
 *
 *  Cuesta una petición más, y la paga con creces: `pendientes` es lo que
 *  alimenta el aviso de «te faltan N datos» de arriba del todo, y ese aviso solo
 *  sirve si aparece sin que nadie lo busque. En la versión anterior había que
 *  abrir Mi ficha para enterarse de que faltaba algo — es decir, había que
 *  sospecharlo primero.
 *
 *  El panel usa esta misma consulta y esta misma clave, así que abrirlo no
 *  vuelve a preguntar. */
export function useMiFicha() {
  return useQuery({ queryKey: claves.ficha, queryFn: () => api.get<MiFicha>('/auth/mi-ficha') });
}

/** Guarda UN campo del perfil público. Recibe el objeto parcial porque el
 *  endpoint acepta actualización parcial, que es lo que hace posible la edición
 *  campo a campo sin arrastrar los otros dos. */
export function useGuardarPerfil() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cambios: Pick<Perfil, 'extension' | 'bio' | 'skills'>) =>
      api.put<Perfil>('/users/me/profile', cambios),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.perfil }),
  });
}

export function useGuardarFicha() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ficha: Record<string, string>) =>
      api.put<{ message?: string }>('/auth/mi-ficha', { ficha }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: claves.ficha });
      qc.invalidateQueries({ queryKey: claves.perfil });
      // El aviso de «te faltan datos» vive en el CROMO, que es la otra raíz de
      // React y no comparte caché con esta: un `invalidateQueries` no le llega.
      // Si ya no falta nada tiene que desaparecer solo.
      //
      // Era `window.APP?.avisoFichaPendiente?.()`. Al pasar el aviso a React ese
      // export desapareció y la llamada —con su `?.`— habría dejado de hacer
      // nada sin dar error: se guardaba la ficha y el aviso se quedaba en
      // pantalla hasta recargar, diciendo que faltan datos que ya están.
      window.dispatchEvent(new CustomEvent(EVENTO_FICHA));
    },
  });
}

export function useCambiarConsentimiento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ docKey, autorizar }: { docKey: string; autorizar: boolean }) =>
      api.post('/legal/accept', { items: [{ doc_key: docKey, accepted: autorizar }] }),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.consentimientos }),
  });
}

export function useCambiarContrasena() {
  return useMutation({
    mutationFn: ({ actual, nueva }: { actual: string; nueva: string }) =>
      api.post('/auth/change-password', { current_password: actual, new_password: nueva }),
  });
}

export function useSubirFoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (archivo: File) => {
      const usuario = Auth.getUser();
      if (!usuario) throw new Error('No hay sesión activa');
      const datos = new FormData();
      // El nombre del campo es `file`: el mismo que usa Uploader.upload y el que
      // espera el endpoint. Cambiarlo devuelve un 422 que no dice cuál falta.
      datos.append('file', archivo);
      return api.upload(`/users/${usuario.id}/avatar`, datos);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.perfil }),
  });
}

/** Antigüedad en la empresa a partir de `hire_date`.
 *
 *  Devuelve null cuando la fecha es futura en vez de inventar «0 meses»: un dato
 *  incoherente en la ficha de alguien es peor que no mostrarlo. */
export function antiguedadDesde(hireDate?: string | null): string | null {
  if (!hireDate || hireDate === 'None') return null;
  const ingreso = new Date(hireDate + 'T00:00:00');
  if (Number.isNaN(ingreso.getTime())) return null;
  const hoy = new Date();
  let meses =
    (hoy.getFullYear() - ingreso.getFullYear()) * 12 + (hoy.getMonth() - ingreso.getMonth());
  if (hoy.getDate() < ingreso.getDate()) meses--;
  if (meses < 0) return null;
  const anios = Math.floor(meses / 12);
  const resto = meses % 12;
  const partes: string[] = [];
  if (anios) partes.push(`${anios} ${anios === 1 ? 'año' : 'años'}`);
  if (resto) partes.push(`${resto} ${resto === 1 ? 'mes' : 'meses'}`);
  return partes.length ? partes.join(' y ') : 'Recién ingresado';
}

export function fechaLarga(iso?: string | null): string | null {
  if (!iso || iso === 'None') return null;
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Cómo se administra un dato del maestro. **Lo decide el servidor**, no esta
 * pantalla.
 *
 * `campo_editable_por_colaborador()` (employee_db_service.py) es la única
 * respuesta a esa pregunta en todo el sistema: la usa el formulario para decidir
 * qué pinta editable y el endpoint para decidir qué acepta. Tener aquí una
 * segunda lista sería exactamente el agujero, porque un campo deshabilitado en
 * el navegador se sigue pudiendo enviar por API — y además se desincronizaría en
 * cuanto Bienestar abriera o cerrara una columna.
 *
 * Por eso esto NO clasifica: lee `editable`, `sensible` y `motivo` tal como
 * llegan y los deja disponibles por clave. El texto del motivo se enseña
 * literal; no se compara con ninguna cadena.
 */
export interface Gestion {
  /** La columna existe en el maestro. */
  existe: boolean;
  /** El colaborador puede escribirla desde Mi ficha. */
  editable: boolean;
  /** Por qué no puede, dicho por el servidor. Se muestra literal. */
  motivo?: string;
  /** Dato sensible: se abre autorizando el tratamiento en Mis consentimientos. */
  sensible: boolean;
  label?: string;
  valor?: string;
}

export function gestionDe(ficha: MiFicha | undefined, clave: string): Gestion {
  const c = ficha?.campos?.find((f) => f.key === clave);
  if (!c) return { existe: false, editable: false, sensible: false };
  const v = ficha?.ficha?.[clave];
  return {
    existe: true,
    editable: Boolean(c.editable),
    motivo: c.motivo,
    sensible: Boolean(c.sensible),
    label: c.label,
    valor: v == null || v === '' ? undefined : String(v),
  };
}

/** Las columnas que ESTE colaborador puede mantener él mismo, hoy.
 *
 *  Se calcula, no se enumera: si Bienestar abre mañana una columna nueva a la
 *  autogestión, aparece sola en «Mis datos». Una lista escrita a mano aquí
 *  dejaría de coincidir con el servidor en cuanto alguien tocara la marca. */
export function camposQueMantieneElColaborador(ficha: MiFicha | undefined): Gestion[] {
  return (ficha?.campos ?? [])
    .filter((c) => c.editable)
    .map((c) => gestionDe(ficha, c.key));
}

/** Resumen de formación. Sale de lo que las dos consultas YA traen: no hay
 *  endpoint de estadísticas y no se añade ninguno. */
export function resumenFormacion(cursos?: CursoDelPerfil[], certificados?: Certificado[]) {
  const lista = cursos ?? [];
  const completados = lista.filter((c) => Boolean(c.completed_at)).length;
  // Promedio de avance, no «porcentaje de completados»: son dos cosas y
  // llamarlas igual infla el número. Sin cursos no es 0 %, es que no aplica.
  const progreso = lista.length
    ? Math.round(lista.reduce((s, c) => s + (c.progress_pct ?? 0), 0) / lista.length)
    : null;
  return {
    inscritos: lista.length,
    completados,
    certificados: certificados?.length ?? 0,
    progreso,
  };
}
